import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";
import store from "@/store";
import Service from "@/services/FornasComplianceService";

@Module({
  store,
  dynamic: true,
  namespaced: true,
  name: "fornas-compliance"
})
class FornasComplianceModule extends VuexModule {
  // --- State ---
  evaluations: any[] = [];
  currentEvaluation: any = null;
  reasonDefinitions: any[] = [];
  drugCatalog: any[] = [];
  activeVersion: any = null;
  loading = false;
  pagination = {
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0
  };
  filters = {
    bulan: new Date().getMonth() + 1,
    tahun: new Date().getFullYear(),
    depo_farmasi: "",
    unit_instalasi: "",
    dokter_id: null as number | null,
    status: "",
    status_resep: "",
    tanggal: ""
  };

  // Reports
  scorecards: any[] = [];
  monthlyReport: any = null;
  dailyRekap: any[] = [];
  trend: any[] = [];

  // --- Mutations ---

  @Mutation SET_EVALUATIONS(val: any[]) {
    this.evaluations = val;
  }

  @Mutation SET_CURRENT_EVALUATION(val: any) {
    this.currentEvaluation = val;
  }

  @Mutation SET_REASON_DEFINITIONS(val: any[]) {
    this.reasonDefinitions = val;
  }

  @Mutation SET_DRUG_CATALOG(val: any[]) {
    this.drugCatalog = val;
  }

  @Mutation SET_ACTIVE_VERSION(val: any) {
    this.activeVersion = val;
  }

  @Mutation SET_LOADING(val: boolean) {
    this.loading = val;
  }

  @Mutation SET_PAGINATION(val: any) {
    this.pagination = val;
  }

  @Mutation SET_FILTERS(val: any) {
    this.filters = { ...this.filters, ...val };
  }

  @Mutation SET_SCORECARDS(val: any[]) {
    this.scorecards = val;
  }

  @Mutation SET_MONTHLY_REPORT(val: any) {
    this.monthlyReport = val;
  }

  @Mutation SET_DAILY_REKAP(val: any[]) {
    this.dailyRekap = val;
  }

  @Mutation SET_TREND(val: any[]) {
    this.trend = val;
  }

  // --- Actions ---

  @Action
  async loadEvaluations(params?: any) {
    this.SET_LOADING(true);
    try {
      const dt = await Service.getEvaluations(params);
      const resp = dt.data;
      this.SET_EVALUATIONS(resp.data?.data || resp.data || []);
      if (resp.meta) {
        this.SET_PAGINATION({
          currentPage: resp.meta.current_page,
          lastPage: resp.meta.last_page,
          perPage: resp.meta.per_page,
          total: resp.meta.total
        });
      }
    } catch (e) {
      console.error("loadEvaluations error:", e);
    }
    this.SET_LOADING(false);
  }

  @Action
  async loadEvaluation(id: number) {
    this.SET_LOADING(true);
    try {
      const dt = await Service.getEvaluation(id);
      this.SET_CURRENT_EVALUATION(dt.data.data || null);
    } catch (e) {
      console.error("loadEvaluation error:", e);
    }
    this.SET_LOADING(false);
  }

  @Action
  async createEvaluation(data: any) {
    try {
      const dt = await Service.createEvaluation(data);
      return dt.data.data || dt.data;
    } catch (e) {
      console.error("createEvaluation error:", e);
      throw e;
    }
  }

  @Action
  async updateEvaluation(payload: { id: number; data: any }) {
    try {
      const dt = await Service.updateEvaluation(payload.id, payload.data);
      return dt.data.data || dt.data;
    } catch (e) {
      console.error("updateEvaluation error:", e);
      throw e;
    }
  }

  @Action
  async deleteEvaluation(id: number) {
    try {
      await Service.deleteEvaluation(id);
    } catch (e) {
      console.error("deleteEvaluation error:", e);
      throw e;
    }
  }

  @Action
  async addItem(payload: { evalId: number; data: any }) {
    try {
      const dt = await Service.addItem(payload.evalId, payload.data);
      return dt.data.data || dt.data;
    } catch (e) {
      console.error("addItem error:", e);
      throw e;
    }
  }

  @Action
  async updateItem(payload: { evalId: number; itemId: number; data: any }) {
    try {
      const dt = await Service.updateItem(payload.evalId, payload.itemId, payload.data);
      return dt.data.data || dt.data;
    } catch (e) {
      console.error("updateItem error:", e);
      throw e;
    }
  }

  @Action
  async deleteItem(payload: { evalId: number; itemId: number }) {
    try {
      await Service.deleteItem(payload.evalId, payload.itemId);
    } catch (e) {
      console.error("deleteItem error:", e);
      throw e;
    }
  }

  @Action
  async signPencatat(payload: { id: number; password: string }) {
    try {
      const dt = await Service.signPencatat(payload.id, payload.password);
      this.SET_CURRENT_EVALUATION(dt.data.data || null);
      return dt.data.data;
    } catch (e) {
      console.error("signPencatat error:", e);
      throw e;
    }
  }

  @Action
  async signKaIfrs(payload: { id: number; password: string }) {
    try {
      const dt = await Service.signKaIfrs(payload.id, payload.password);
      this.SET_CURRENT_EVALUATION(dt.data.data || null);
      return dt.data.data;
    } catch (e) {
      console.error("signKaIfrs error:", e);
      throw e;
    }
  }

  @Action
  async signApprover(payload: { id: number; password: string }) {
    try {
      const dt = await Service.signApprover(payload.id, payload.password);
      this.SET_CURRENT_EVALUATION(dt.data.data || null);
      return dt.data.data;
    } catch (e) {
      console.error("signApprover error:", e);
      throw e;
    }
  }

  @Action
  async loadDrugCatalog(params?: any) {
    try {
      const dt = await Service.getDrugCatalog(params);
      this.SET_DRUG_CATALOG(dt.data.data || []);
    } catch (e) {
      console.error("loadDrugCatalog error:", e);
    }
  }

  @Action
  async loadActiveVersion() {
    try {
      const dt = await Service.getActiveVersion();
      this.SET_ACTIVE_VERSION(dt.data.data || null);
    } catch (e) {
      console.error("loadActiveVersion error:", e);
    }
  }

  @Action
  async loadDailyRekap(params: any) {
    try {
      const dt = await Service.getDailyRekap(params);
      this.SET_DAILY_REKAP(dt.data.data || []);
    } catch (e) {
      console.error("loadDailyRekap error:", e);
    }
  }

  @Action
  async loadDailyBreakdown(params: any) {
    try {
      const dt = await Service.getDailyBreakdown(params);
      this.SET_DAILY_REKAP(dt.data.data || []);
    } catch (e) {
      console.error("loadDailyBreakdown error:", e);
    }
  }

  @Action
  async loadMonthlyReport(params: any) {
    try {
      const dt = await Service.getMonthlyReport(params);
      this.SET_MONTHLY_REPORT(dt.data.data || null);
    } catch (e) {
      console.error("loadMonthlyReport error:", e);
    }
  }

  @Action
  async loadScorecard(params: any) {
    try {
      const dt = await Service.getScorecard(params);
      this.SET_SCORECARDS(dt.data.data || []);
    } catch (e) {
      console.error("loadScorecard error:", e);
    }
  }

  @Action
  async loadTrend(params: any) {
    try {
      const dt = await Service.getTrend(params);
      this.SET_TREND(dt.data.data || []);
    } catch (e) {
      console.error("loadTrend error:", e);
    }
  }

  @Action
  setFilters(filters: any) {
    this.SET_FILTERS(filters);
  }
}

export default getModule(FornasComplianceModule);
